import {
  type BuyabilityBlockedReason,
  getBuyabilityBlockedReason,
} from '@lib/pharma/buyability';
import type { PharmaVariantMetadata } from '@lib/types/pharma';

type VariantForConsultation = {
  metadata?: Record<string, unknown> | null;
};

export function requiresConsultation(
  variant: VariantForConsultation | undefined | null,
): boolean {
  if (!variant) {
    return false;
  }
  const meta = variant.metadata as PharmaVariantMetadata | undefined;
  return Boolean(meta?.questionnaire_id);
}

export function getQuestionnaireSlug(
  variant: VariantForConsultation | undefined | null,
): string | null {
  if (!variant) {
    return null;
  }
  const meta = variant.metadata as PharmaVariantMetadata | undefined;
  return meta?.questionnaire_id ?? null;
}

export type ConsultationMode = 'modal' | 'full_page';

/**
 * Returns the consultation flow a variant should trigger, derived from its
 * legal classification:
 *   - 'modal'     — P med inline modal
 *   - 'full_page' — POM full-page wizard
 *   - null        — no consultation required (GSL or missing questionnaire)
 */
export function getConsultationMode(
  variant: VariantForConsultation | undefined | null,
): ConsultationMode | null {
  if (!variant) {
    return null;
  }
  const meta = variant.metadata as PharmaVariantMetadata | undefined;
  if (!meta?.questionnaire_id) {
    return null;
  }
  return classificationToMode(meta.legal_classification);
}

function classificationToMode(
  classification: PharmaVariantMetadata['legal_classification'],
): ConsultationMode | null {
  if (classification === 'P') {
    return 'modal';
  }
  if (classification === 'POM') {
    return 'full_page';
  }
  return null;
}

/**
 * The buy-flow state the PDP CTA should render. Centralising this here keeps
 * the rules covered by Layer 1 unit tests rather than scattering them across
 * the desktop and mobile CTA components.
 *
 *   - 'mounting'      — pre-mount/loading. Disabled placeholder so the
 *                       button never flashes the wrong text on hydrate.
 *   - 'no-variant'    — multi-variant product with no option picked yet.
 *   - 'unclassified'  — variant has no legal_classification (pharmacy
 *                       safety: default to deny). Also covers a P/POM
 *                       variant that's missing its questionnaire_id.
 *   - 'not-for-sale'  — variant published for SEO but not merch-reviewed
 *                       (not_for_sale flag). Page stays live; never buyable.
 *   - 'gsl'           — direct add-to-basket allowed (GSL classification).
 *   - 'other'         — direct add-to-basket allowed (Other/unregulated
 *                       classification, e.g. cosmetics or supplements).
 *   - 'consultation'  — P or POM, requires consultation completion before
 *                       the line can be added. Mode tells us which UI.
 */
export type CTAState =
  | { kind: 'mounting' }
  | { kind: 'no-variant' }
  | { kind: 'unclassified' }
  | { kind: 'not-for-sale' }
  | { kind: 'gsl' }
  | { kind: 'other' }
  | { kind: 'consultation'; mode: ConsultationMode };

export function getCTAState(
  mounted: boolean,
  variant: VariantForConsultation | undefined | null,
): CTAState {
  if (!mounted) {
    return { kind: 'mounting' };
  }
  if (!variant) {
    return { kind: 'no-variant' };
  }
  // The shared @lib predicate owns the not-for-sale/classification/
  // questionnaire gate; this function only adds the UI-facing kinds
  // (mounting, no-variant, and telling GSL apart from OTHER/consultation).
  const blockedReason = getBuyabilityBlockedReason(variant);
  if (blockedReason === 'not-for-sale') {
    return { kind: 'not-for-sale' };
  }
  if (blockedReason) {
    return { kind: 'unclassified' };
  }
  const meta = variant.metadata as PharmaVariantMetadata | undefined;
  const classification = meta?.legal_classification;
  if (classification === 'GSL') {
    return { kind: 'gsl' };
  }
  if (classification === 'OTHER') {
    return { kind: 'other' };
  }
  if (classification === 'P') {
    return { kind: 'consultation', mode: 'modal' };
  }
  if (classification === 'POM') {
    return { kind: 'consultation', mode: 'full_page' };
  }
  // Unreachable while the shared predicate owns the gate — it only returns
  // null for GSL/OTHER or a P/POM with a questionnaire, all handled above.
  // Kept as the safety net if the two ever diverge: default to deny.
  return { kind: 'unclassified' };
}

/**
 * True when a variant is blocked from purchase by its own metadata — either
 * flagged `not_for_sale`, or missing the classification / questionnaire data
 * needed to sell it (the 'not-for-sale' and 'unclassified' CTA states). In
 * these states the CTA is hard-disabled, so an "In Stock" badge would
 * contradict the page and should be suppressed. Derived straight from the
 * variant (not the mount-gated CTA state) so the badge decision is stable
 * across SSR/hydrate and never flashes.
 */
export function isBlockedByMetadata(
  variant: VariantForConsultation | undefined | null,
): boolean {
  if (!variant) {
    return false;
  }
  return getBuyabilityBlockedReason(variant) !== null;
}

/**
 * True when the CTA's `not-for-sale`/`unclassified` state must win over a
 * stock-based CTA (loading, in-stock, out-of-stock, max-quantity). A
 * metadata block is permanent and independent of stock level, so it must
 * never be overwritten once a live/async stock check resolves — including
 * when that variant also happens to read 0 stock. Both the desktop and
 * mobile CTA containers hoist their stock-based rendering outside
 * `getCTAState`'s own branching, which is exactly the gap that let a
 * genuinely blocked variant flicker from "Not available to purchase" to
 * "Out of stock" once the async stock check resolved.
 */
export function isMetadataBlockedState(kind: CTAState['kind']): boolean {
  return kind === 'not-for-sale' || kind === 'unclassified';
}

/**
 * The CTA-state kind that should drive the metadata-block notice shown
 * beneath a disabled CTA, accounting for a multi-variant product where no
 * option has been picked yet ('no-variant'). If every variant is blocked the
 * same way ('not-for-sale' or 'unclassified'), the shopper hits that wall
 * regardless of what they pick, so it surfaces up front rather than waiting
 * for a selection. In every other case — a variant is already selected, or
 * variants are mixed/uniformly sellable — the caller only needs to tell
 * apart 'not-for-sale' and 'unclassified' from "no notice", so this always
 * returns one of those two or 'no-variant'; it never surfaces a sellable
 * kind (e.g. 'gsl') early just because every variant happens to share it.
 */
export function getMetadataBlockKind(
  selectedVariant: VariantForConsultation | undefined | null,
  allVariants: VariantForConsultation[] | undefined | null,
): 'no-variant' | 'not-for-sale' | 'unclassified' {
  const kind = getCTAState(true, selectedVariant).kind;
  if (kind === 'not-for-sale' || kind === 'unclassified') {
    return kind;
  }
  if (kind !== 'no-variant') {
    return 'no-variant';
  }
  const variantKinds = (allVariants ?? []).map(
    (v) => getCTAState(true, v).kind,
  );
  const [firstKind] = variantKinds;
  if (
    (firstKind === 'not-for-sale' || firstKind === 'unclassified') &&
    variantKinds.every((k) => k === firstKind)
  ) {
    return firstKind;
  }
  return 'no-variant';
}

/**
 * Why a variant landed in the 'unclassified' CTA state, so a deliberate
 * block can be told apart from broken data:
 *   - 'missing-questionnaire'  — has a P/POM classification but no
 *                                questionnaire_id, so it can't run the
 *                                consultation. The product is meant to be
 *                                sold and isn't — a likely lost sale.
 *   - 'missing-classification' — no legal_classification at all; may be a
 *                                product that was never set up for sale.
 *   - null                     — the variant is not in the unclassified state.
 */
export type UnclassifiedReason =
  | 'missing-questionnaire'
  | 'missing-classification';

export function getUnclassifiedReason(
  variant: VariantForConsultation | undefined | null,
): UnclassifiedReason | null {
  if (!variant) {
    return null;
  }
  const reason = getBuyabilityBlockedReason(variant);
  if (
    reason === 'missing-classification' ||
    reason === 'missing-questionnaire'
  ) {
    return reason;
  }
  return null;
}

/**
 * Why a live PDP variant can't be purchased, for the data-hygiene signal, or
 * null when it's sellable. `not-for-sale` is a deliberate block; the
 * unclassified reasons flag broken data (a `missing-questionnaire` P/POM
 * product is a likely lost sale). Alias of the shared @lib predicate's
 * reason type — kept as its own name here since callers in this module think
 * in terms of "why is this unbuyable", not "buyability blocked reason".
 */
export type UnbuyableReason = BuyabilityBlockedReason;

function getUnbuyableReason(
  variant: VariantForConsultation | undefined | null,
): UnbuyableReason | null {
  return getBuyabilityBlockedReason(variant);
}

/** Variant fields the unbuyable-variant event payload is built from. */
type VariantForUnbuyableEvent = VariantForConsultation & {
  id?: string;
  sku?: string | null;
};

export type UnbuyableVariantEvent = {
  product_handle: string;
  variant_id: string;
  variant_sku?: string;
  reason: UnbuyableReason;
  legal_classification?: string;
};

/**
 * Builds the `pdp_unbuyable_variant_viewed` payload for a variant, or null
 * when the variant is sellable (or not yet selected) and nothing should fire.
 * Pure so the PDP component's effect stays trivial and the branching is
 * covered by unit tests rather than an untested inline closure.
 */
export function getUnbuyableVariantEvent(
  variant: VariantForUnbuyableEvent | undefined | null,
  productHandle: string | null | undefined,
): UnbuyableVariantEvent | null {
  const reason = getUnbuyableReason(variant);
  const variantId = variant?.id;
  if (!(reason && variantId)) {
    return null;
  }
  const meta = variant?.metadata as PharmaVariantMetadata | undefined;
  return {
    product_handle: productHandle ?? '',
    variant_id: variantId,
    variant_sku: variant?.sku ?? undefined,
    reason,
    legal_classification: meta?.legal_classification,
  };
}

/**
 * Shopper-facing explanation for why a disabled CTA can't be used, shown
 * beneath the button so an "In Stock" product doesn't dead-end without a
 * reason. Returns null for CTA states that are self-explanatory (loading,
 * option not yet picked), purchasable, or already explained elsewhere —
 * 'not-for-sale' has its own dedicated notice (`NotForSaleNotice`), rendered
 * alongside the CTA instead of duplicating the message here.
 */
export function ctaUnavailableReason(kind: CTAState['kind']): string | null {
  if (kind === 'unclassified') {
    return 'This product is currently unavailable to buy online.';
  }
  return null;
}
